const PREFIX = '[PlayaYield]';

let debugEnabled = false;

/**
 * Checks for a runtime debug override via localStorage.
 * Works in extension pages (popup, sidepanel) where localStorage is available.
 * Background service workers do not have localStorage, so this safely returns false there.
 *
 * To enable at runtime without changing code:
 *   localStorage.setItem('playayield:debug', 'true')   // enable
 *   localStorage.removeItem('playayield:debug')         // disable
 */
function isRuntimeDebugEnabled(): boolean {
  try {
    return localStorage.getItem('playayield:debug') === 'true';
  } catch {
    return false;
  }
}

function shouldLog(): boolean {
  return debugEnabled || isRuntimeDebugEnabled();
}

/**
 * Enable or disable verbose SDK logging.
 * Called internally by initializePlayaYield() based on the `debug` config option.
 */
export function setDebugMode(enabled: boolean): void {
  debugEnabled = enabled;
}

/**
 * Internal SDK logger.
 *
 * - `log` and `warn` are gated behind the `debug` flag (or the `playayield:debug` localStorage key).
 * - `error` is always emitted — errors should never be silenced.
 *
 * @example
 * // In your background script:
 * initializePlayaYield({ apiKey: 'xxx', debug: true });
 *
 * // Or toggle at runtime in DevTools console (extension pages only):
 * localStorage.setItem('playayield:debug', 'true');
 */
export const logger = {
  log(message: string, ...args: any[]): void {
    if (shouldLog()) {
      console.log(`${PREFIX} ${message}`, ...args);
    }
  },

  warn(message: string, ...args: any[]): void {
    if (shouldLog()) {
      console.warn(`${PREFIX} ${message}`, ...args);
    }
  },

  error(message: string, ...args: any[]): void {
    console.error(`${PREFIX} ${message}`, ...args);
  },
};
